import countries from "world-countries";
import { CountryDTO } from "../../../shared/dtos/countryDTO";

interface Indexed {
  [key: string]: CountryDTO | undefined;
}

type WorldCountry = {
  cca2?: string;
  idd?: {
    root?: string;
    suffixes?: string[];
  };
  name?: {
    common?: string;
  };
};

const COUNTRY_NAME_OVERRIDES: Record<string, string> = {
  Brazil: "Brasil",
  France: "Francia",
  Germany: "Alemania",
  Japan: "Japon",
  Mexico: "Mexico",
  Peru: "Peru",
  Spain: "Espana",
  "United Kingdom": "Reino Unido",
  "United States": "Estados Unidos",
};

const COUNTRY_NAME_ALIASES: Record<string, string> = {
  "ee.uu": "Estados Unidos",
  "eeuu": "Estados Unidos",
  "espana": "Espana",
  "estados unidos de america": "Estados Unidos",
  "gran bretana": "Reino Unido",
  "japon": "Japon",
  "mexico": "Mexico",
  "peru": "Peru",
  "reino unido": "Reino Unido",
  "suriname": "Surinam",
  uk: "Reino Unido",
  usa: "Estados Unidos",
};

const toNormalizedText = (value: string): string =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

const resolveCountryName = (country: WorldCountry): string => {
  const commonName = country.name?.common?.trim();
  if (!commonName) return "";
  return COUNTRY_NAME_OVERRIDES[commonName] ?? commonName;
};

const resolvePhoneCode = (country: WorldCountry): string => {
  const root = country.idd?.root;
  if (!root) return "";

  const suffix = country.idd?.suffixes?.[0] ?? "";
  return `${root}${suffix}`;
};

const deduplicatedCountries = new Map<string, CountryDTO>();

(countries as WorldCountry[]).forEach((country) => {
  const code = country.cca2?.trim().toUpperCase();
  const countryName = resolveCountryName(country);

  if (!countryName || !code || deduplicatedCountries.has(code)) {
    return;
  }

  deduplicatedCountries.set(code, {
    country: countryName,
    code,
    phoneCode: resolvePhoneCode(country),
  });
});

export const countriesList: CountryDTO[] = Array.from(
  deduplicatedCountries.values(),
).sort((first, second) =>
  String(first.country).localeCompare(String(second.country), "es", {
    sensitivity: "base",
  }),
);

export const indexedCountriesByName = countriesList.reduce(
  (result: Indexed, el: CountryDTO) => {
    const countryName = String(el.country);
    result[countryName] = el;
    return result;
  },
  {},
);

const indexedCountriesByNormalizedName = countriesList.reduce(
  (result: Indexed, el: CountryDTO) => {
    const countryName = String(el.country);
    result[toNormalizedText(countryName)] = el;
    return result;
  },
  {},
);

export const getCanonicalCountryName = (
  countryName?: string,
): string | undefined => {
  if (!countryName) return countryName;

  const normalizedCountryName = toNormalizedText(countryName);
  const aliasedCountryName = COUNTRY_NAME_ALIASES[normalizedCountryName];

  if (aliasedCountryName && indexedCountriesByName[aliasedCountryName]) {
    return aliasedCountryName;
  }

  if (indexedCountriesByName[countryName]) {
    return countryName;
  }

  return indexedCountriesByNormalizedName[normalizedCountryName]?.country as
    | string
    | undefined;
};

export const getCountryByName = (countryName?: string): CountryDTO | undefined => {
  const canonicalCountryName = getCanonicalCountryName(countryName);
  if (!canonicalCountryName) return undefined;
  return indexedCountriesByName[canonicalCountryName];
};

export const getFlagEmojiFromIso2 = (countryCode?: string): string => {
  if (!countryCode || countryCode.length !== 2) return "";

  return countryCode
    .toUpperCase()
    .split("")
    .map((letter) => String.fromCodePoint(127397 + letter.charCodeAt(0)))
    .join("");
};
